import React, { useState, useRef, useEffect } from 'react';
import { Send, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';
import PreApprovalWizard from './PreApprovalWizard';
import { usePodPanel } from '../../pages/LandingPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Funding Assistant Avatar (Animated GIF)
const ASSISTANT_AVATAR = "/funding_assistant_avatar.gif";

// Generate a unique session ID for this browser session
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('chatSessionId');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('chatSessionId', sessionId);
  }
  return sessionId;
};

const GREETING_MESSAGE = "Hello! I'm your One Force Live Funding Assistant. I'm here to help you understand the $120K Co-Op Funding, the AI Horse-Powered platform, and guide you through every step of the funding process. How can I help you today?";

const ChatSection = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      text: GREETING_MESSAGE,
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId] = useState(getSessionId);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const { openPodPanel } = usePodPanel();

  const scrollToBottom = () => {
    // Only scroll within the chat container, not the whole page
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  // Track if this is the initial render
  const isInitialMount = useRef(true);

  useEffect(() => {
    // Skip scrolling on initial mount to prevent page jump
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isTyping) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: inputValue.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);
    setError(null);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: userMessage.text,
        session_id: sessionId
      });

      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        text: response.data.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (err) {
      console.error('Chat error:', err);
      setError('Sorry, I encountered an error. Please try again.');
      const errorMessage = {
        id: Date.now() + 1,
        type: 'bot',
        text: "I'm sorry, I'm having trouble connecting right now. Please try again in a moment.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = async () => {
    try {
      await axios.delete(`${API}/chat/session/${sessionId}`);
      setMessages([
        {
          id: Date.now(),
          type: 'bot',
          text: GREETING_MESSAGE,
          timestamp: new Date()
        }
      ]);
      setError(null);
    } catch (err) {
      console.error('Error clearing chat:', err);
    }
  };

  const handleWizardComplete = (data) => {
    console.log('Wizard completed:', data);
  };

  return (
    <section id="funding-assistant" className="py-12 md:py-16 bg-slate-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent" />
        <div className="absolute top-1/2 left-0 w-[400px] h-[400px] bg-emerald-500/5 rounded-full blur-3xl -translate-y-1/2" />
        <div className="absolute top-1/2 right-0 w-[400px] h-[400px] bg-cyan-500/5 rounded-full blur-3xl -translate-y-1/2" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2">
            Get Started with Your <span className="text-emerald-400">$120K Funding</span>
          </h2>
          <p className="text-slate-400 text-sm md:text-base">
            Complete your pre-approval below, then ask our Funding Assistant any questions
          </p>
        </div>

        {/* Full-Width Wizard - Above Chat */}
        <div className="mb-8">
          <h3 className="text-white font-semibold mb-3 flex items-center text-sm">
            <span className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center text-slate-950 text-xs font-bold mr-2">1</span>
            Start Your Pre-Approval
          </h3>
          <PreApprovalWizard 
            isOpen={true}
            embedded={true}
            onComplete={handleWizardComplete}
            openPodPanel={openPodPanel}
          />
        </div>

        {/* Chat Section - Below Wizard */}
        <div>
          <h3 className="text-white font-semibold mb-3 flex items-center text-sm">
            <span className="w-6 h-6 bg-cyan-500 rounded-full flex items-center justify-center text-slate-950 text-xs font-bold mr-2">?</span>
            Ask the Funding Assistant
          </h3>
          
          {/* Chat Container */}
          <div className="bg-slate-950 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden h-[400px] flex flex-col">
            {/* Chat Header */}
            <div className="bg-slate-800 px-4 py-3 flex items-center justify-between border-b border-slate-700 flex-shrink-0">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full overflow-hidden ring-2 ring-emerald-500">
                  <img 
                    src={ASSISTANT_AVATAR} 
                    alt="Funding Assistant" 
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm">Funding Assistant</h3>
                  <p className="text-green-400 text-xs flex items-center">
                    <span className="w-2 h-2 bg-green-400 rounded-full mr-1.5 animate-pulse"></span>
                    Online
                  </p>
                </div>
              </div>
              <button
                onClick={clearChat}
                className="text-slate-400 hover:text-white transition-colors p-2 hover:bg-slate-700 rounded-lg"
                title="Clear chat"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {message.type === 'bot' && (
                    <div className="w-7 h-7 rounded-full overflow-hidden mr-2 flex-shrink-0 ring-1 ring-emerald-500/50">
                      <img 
                        src={ASSISTANT_AVATAR} 
                        alt="Assistant" 
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  )}
                  <div
                    className={`max-w-[85%] rounded-2xl px-3 py-2 ${
                      message.type === 'user'
                        ? 'bg-emerald-500 text-slate-950'
                        : 'bg-slate-800 text-slate-200'
                    }`}
                  >
                    <p className="text-xs whitespace-pre-wrap">{message.text}</p>
                  </div>
                </div>
              ))}

              {isTyping && (
                <div className="flex justify-start">
                  <div className="w-7 h-7 rounded-full overflow-hidden mr-2 flex-shrink-0 ring-1 ring-emerald-500/50">
                    <img 
                      src={ASSISTANT_AVATAR} 
                      alt="Assistant" 
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="bg-slate-800 rounded-2xl px-3 py-2">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="border-t border-slate-700 p-3 bg-slate-800/50 flex-shrink-0">
              <div className="flex items-center space-x-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask about funding, pricing..."
                  className="flex-1 bg-slate-900 border border-slate-600 rounded-xl px-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-xs"
                  disabled={isTyping}
                />
                <Button
                  onClick={sendMessage}
                  disabled={!inputValue.trim() || isTyping}
                  className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 rounded-xl px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              {error && (
                <p className="text-red-400 text-xs mt-2">{error}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ChatSection;
